
'use client';

import { useEffect } from 'react';
import { metrics, trace, type Span } from '@opentelemetry/api';
import { logs } from '@opentelemetry/api-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import {
  LoggerProvider,
  BatchLogRecordProcessor,
} from '@opentelemetry/sdk-logs';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';

const OtelProvider = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    // This effect should only run once.
    if (typeof window !== 'undefined' && !(window as any).otel_initialized) {
        console.log('Initializing OpenTelemetry for Web...');
        
        const resource = new Resource({
          [SemanticResourceAttributes.SERVICE_NAME]: 'konstantins-voyage-web',
        });
    
        const otlpEndpoint = window.location.origin;

        // Trace provider
        const traceExporter = new OTLPTraceExporter({ url: `${otlpEndpoint}/api/v1/traces` });
        const tracerProvider = new WebTracerProvider({ resource });
        tracerProvider.addSpanProcessor(new BatchSpanProcessor(traceExporter));
        trace.setGlobalTracerProvider(tracerProvider);

        // Meter provider
        const metricExporter = new OTLPMetricExporter({ url: `${otlpEndpoint}/api/v1/metrics` });
        const meterProvider = new MeterProvider({ resource });
        meterProvider.addMetricReader(new PeriodicExportingMetricReader({
            exporter: metricExporter,
            exportIntervalMillis: 5000,
        }));
        metrics.setGlobalMeterProvider(meterProvider);

        // Logger provider
        const logExporter = new OTLPLogExporter({ url: `${otlpEndpoint}/api/v1/logs` });
        const loggerProvider = new LoggerProvider({ resource });
        loggerProvider.addLogRecordProcessor(new BatchLogRecordProcessor(logExporter));
        logs.setGlobalLoggerProvider(loggerProvider);
        
        // Auto-instrumentations
        registerInstrumentations({
          instrumentations: [getWebAutoInstrumentations({
            '@opentelemetry/instrumentation-user-interaction': {
                enabled: true,
                // Add a hook to process spans and truncate long attributes
                spanHook: (span: Span, { event, element }) => {
                    const MAX_ATTR_LENGTH = 100;
                    if (element?.className && typeof element.className === 'string' && element.className.length > MAX_ATTR_LENGTH) {
                        span.setAttribute('element.class_name_truncated', element.className.substring(0, MAX_ATTR_LENGTH));
                    }
                }
            },
            '@opentelemetry/instrumentation-xml-http-request': {
                propagateTraceHeaderCorsUrls: [/.+/g],
            },
            '@opentelemetry/instrumentation-fetch': {
                propagateTraceHeaderCorsUrls: [/.+/g],
            },
          })],
        });

        console.log('OpenTelemetry for Web initialized.');
        (window as any).otel_initialized = true;

        const logger = logs.getLogger('konstantins-voyage-web-errors');

        window.addEventListener('error', (event: ErrorEvent) => {
            console.error('Uncaught error captured:', event.error);
            logger.emit({
                // SeverityNumber.ERROR
                severityNumber: 17,
                body: `Uncaught Browser Error: ${event.message}`,
                attributes: {
                    'error.type': event.error?.name || 'Error',
                    'error.stack': event.error?.stack || 'No stack trace available',
                    'error.filename': event.filename,
                    'error.lineno': event.lineno,
                    'error.colno': event.colno,
                    'component': 'global-error-handler',
                },
            });
            // Flush logs immediately on error.
            loggerProvider.forceFlush().catch(err => console.error('Error flushing logs:', err));
        });

        window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
            console.error('Unhandled promise rejection captured:', event.reason);
            const reason = event.reason;
            const body = reason instanceof Error 
                ? `Unhandled Promise Rejection: ${reason.message}` 
                : `Unhandled Promise Rejection: ${String(reason)}`;
            
            logger.emit({
                // SeverityNumber.ERROR
                severityNumber: 17,
                body: body,
                attributes: {
                    'error.type': reason instanceof Error ? reason.name : 'PromiseRejection',
                    'error.stack': reason instanceof Error ? reason.stack : 'No stack trace available',
                    'component': 'global-unhandled-rejection-handler',
                },
            });
            // Flush logs immediately on error.
            loggerProvider.forceFlush().catch(err => console.error('Error flushing logs:', err));
        });
    }
  }, []);

  return <>{children}</>;
};

export default OtelProvider;
